import { useRef, useMemo, useState, useCallback } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { type ColumnFiltersState, type OnChangeFn, type PaginationState, type SortingState } from '@tanstack/react-table';
import { Table, Grid3X3 } from 'lucide-react';

import { useAllTenantsIntegrations, useIntegrationDefinitions, type TenantIntegrationInstance } from '@/hooks/use-integrations';
import { useTenants } from '@/hooks/use-tenants';
import { createIntegrationColumns, type IntegrationsTableMeta } from '@/components/integrations/columns';
import { IntegrationCards } from '@/components/integrations/integration-cards';
import { IntegrationsGrid } from '@/components/integrations/integrations-grid';
import { DataTable, DataTableToolbar, useDataTable, type DataTableToolbarRef } from '@/components/common/data-table';
import { Page, PageContent, PageHeader, PageTitle } from '@/components/common/page';
import { Button } from '@/components/ui/button';
import { TooltipProvider } from '@/components/ui/tooltip';

export const Route = createFileRoute('/integrations')({
  component: IntegrationsPage,
});

type ViewMode = 'grid' | 'table';

function IntegrationsPage() {
  const toolbarRef = useRef<DataTableToolbarRef | null>(null);
  const [selectedIntegration, setSelectedIntegration] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('table');

  const { data: tenants } = useTenants();
  const { data: allInstances, isLoading, refetch } = useAllTenantsIntegrations();
  const { data: definitions } = useIntegrationDefinitions();

  const tenantMap = useMemo(() => {
    if (!tenants) return new Map<string, string>();
    return new Map(tenants.map(t => [t.id, t.company || t.id.slice(0, 8)]));
  }, [tenants]);

  const definitionMap = useMemo(() => {
    if (!definitions) return new Map<string, string>();
    return new Map(definitions.map(d => [d.id, d.name]));
  }, [definitions]);

  const tenantCounts = useMemo(() => {
    const counts = new Map<string, Set<string>>();
    if (!allInstances) return new Map<string, number>();
    for (const instance of allInstances) {
      const set = counts.get(instance.integration_id) ?? new Set();
      set.add(instance.tenant_id);
      counts.set(instance.integration_id, set);
    }
    const result = new Map<string, number>();
    for (const [key, set] of counts) {
      result.set(key, set.size);
    }
    return result;
  }, [allInstances]);

  const totalUniqueTenants = useMemo(() => {
    if (!allInstances) return 0;
    return new Set(allInstances.map(i => i.tenant_id)).size;
  }, [allInstances]);

  const filteredByCard = useMemo(() => {
    if (!allInstances) return [];
    if (!selectedIntegration) return allInstances;
    return allInstances.filter(i => i.integration_id === selectedIntegration);
  }, [allInstances, selectedIntegration]);

  const columns = useMemo(
    () => createIntegrationColumns(tenants, definitions),
    [tenants, definitions],
  );

  const [sorting, setSorting] = useState<SortingState>([{ id: 'tenant_id', desc: false }]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 50 });

  const handleSelectIntegration = useCallback((id: string | null) => {
    setSelectedIntegration(id);
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  }, []);

  const handleColumnFiltersChange: OnChangeFn<ColumnFiltersState> = useCallback((updater) => {
    setColumnFilters(updater);
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  }, []);

  const filteredData = useMemo(() => {
    let data = filteredByCard;

    for (const filter of columnFilters) {
      const values = filter.value as string[];
      if (values.length > 0) {
        data = data.filter(row => {
          const rowValue = row[filter.id as keyof TenantIntegrationInstance];
          return values.includes(String(rowValue));
        });
      }
    }

    if (sorting.length > 0) {
      const { id, desc } = sorting[0];
      data = [...data].sort((a, b) => {
        let aVal = String(a[id as keyof TenantIntegrationInstance] ?? '');
        let bVal = String(b[id as keyof TenantIntegrationInstance] ?? '');
        if (id === 'tenant_id') {
          aVal = tenantMap.get(a.tenant_id) ?? aVal;
          bVal = tenantMap.get(b.tenant_id) ?? bVal;
        }
        if (id === 'integration_id') {
          aVal = definitionMap.get(a.integration_id) ?? aVal;
          bVal = definitionMap.get(b.integration_id) ?? bVal;
        }
        return desc ? bVal.localeCompare(aVal) : aVal.localeCompare(bVal);
      });
    }

    return data;
  }, [filteredByCard, columnFilters, sorting, tenantMap, definitionMap]);

  const paginatedData = useMemo(() => {
    const start = pagination.pageIndex * pagination.pageSize;
    return filteredData.slice(start, start + pagination.pageSize);
  }, [filteredData, pagination]);

  const tableMeta: IntegrationsTableMeta = useMemo(
    () => ({ tenants, definitions }),
    [tenants, definitions],
  );

  const table = useDataTable({
    name: 'integrations',
    columns,
    data: paginatedData,
    pageCount: Math.ceil(filteredData.length / pagination.pageSize),
    rowCount: filteredData.length,
    state: { sorting, columnFilters, pagination },
    onSortingChange: setSorting as OnChangeFn<SortingState>,
    onColumnFiltersChange: handleColumnFiltersChange,
    onPaginationChange: setPagination as OnChangeFn<PaginationState>,
    meta: tableMeta,
  });

  return (
    <TooltipProvider>
      <Page>
        <PageHeader>
          <PageTitle>Integrations</PageTitle>
        </PageHeader>
        {definitions && (
          <IntegrationCards
            integrations={definitions}
            selectedId={selectedIntegration}
            onSelect={handleSelectIntegration}
            tenantCounts={tenantCounts}
            totalTenants={totalUniqueTenants}
          />
        )}
        <div className="flex items-center justify-between gap-2">
          <div className="flex-1">
            <DataTableToolbar
              ref={toolbarRef}
              table={table}
              isLoading={isLoading}
              onRefresh={() => void refetch()}
            />
          </div>
          <div className="flex items-center border rounded-md">
            <Button
              variant={viewMode === 'table' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => { setViewMode('table'); }}
              className="rounded-r-none border-r"
            >
              <Table className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => { setViewMode('grid'); }}
              className="rounded-l-none"
            >
              <Grid3X3 className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <PageContent>
          {viewMode === 'grid' ? (
            <IntegrationsGrid
              instances={paginatedData}
              tenants={tenants}
              definitions={definitions}
            />
          ) : (
            <DataTable
              table={table}
              totalCount={filteredData.length}
              isLoading={isLoading}
              scope="integrations"
              toolbarRef={toolbarRef}
            />
          )}
        </PageContent>
      </Page>
    </TooltipProvider>
  );
}
